import { EventEmitter } from 'events';
import isFunction from 'lodash/isFunction';
import each from 'lodash/each';
import { CHANGE } from './const';
import { register } from './dispatcher';
import {
  ActionTypes,
  AlertTimeout,
  AlertTypes,
} from './constants/MogamboConstants';

export function createStore(spec) {
  const emitter = new EventEmitter();
  emitter.setMaxListeners(1000);

  const store = {
    emitChange() {
      emitter.emit(CHANGE);
    },

    addChangeListener(callback) {
      emitter.on(CHANGE, callback);
    },

    removeChangeListener(callback) {
      emitter.removeListener(CHANGE, callback);
    },
    ...spec,
  };

  // Auto-bind store methods for convenience
  each(store, (val, key) => {
    if (isFunction(val)) {
      store[key] = store[key].bind(store);
    }
  });

  return store;
}

const ActionStore = createStore({
  _allPopups: {
    'create-bot-popup': false,
    'create-new-story-popup': false,
    'confirmation-dialog-popup': false,
  },

  _allOverlays: {
    'general-responses-overlay': false,
    'step-priority-overlay': false,
  },

  _userResponseContext: null,

  _loadStatus: {
    loading: false,
    message: null,
    numberLoaders: 0,
  },

  _alertStatus: {
    active: false,
    alertLength: 0,
    message: null,
    messageType: 'POSITIVE',
  },

  _confirmationDialog: {
    active: false,
    successCallback: null,
    data: null,
    title: null,
    message: null,
  },

  _componentModalStatus: {
    active: false,
    loading: false,
    children: null,
    isV2: false,
    onModalClose: () => null,
  },

  _headerData: {
    actions: [],
    brandsList: [],
  },

  _componentTooltipStatus: {
    active: false,
    positionX: 0,
    positionY: 0,
    children: null,
    type: 'BOTTOM',
  },

  _searchOverlayStatus: {
    active: false,
    status: 'INITIAL',
    results: [],
  },

  _createBotModalStatus: {
    created: false,
  },

  _createTemplateModalStatus: {
    created: false,
  },

  setPopups(id, status) {
    this._allPopups[id] = status;
  },

  getPopups() {
    return this._allPopups;
  },

  setOverlays(id, status) {
    this._allOverlays[id] = status;
  },

  getOverlays() {
    return this._allOverlays;
  },

  setUserResponseContext(context) {
    this._userResponseContext = context;
  },

  getUserResponseContext() {
    return this._userResponseContext;
  },

  getAlertStatus() {
    return this._alertStatus;
  },

  setAlertStatus(status, message, alertLength, messageType) {
    // to make sure message always gets treated like a string
    this._alertStatus.message = `${message}`;
    this._alertStatus.active = status;
    this._alertStatus.alertLength = alertLength;
    this._alertStatus.messageType = messageType;
  },

  getLoadStatus() {
    return this._loadStatus;
  },

  setLoadStatus(status, message) {
    if (status) {
      this._loadStatus.numberLoaders = this._loadStatus.numberLoaders + 1;
    } else if (this._loadStatus.numberLoaders > 0) {
      this._loadStatus.numberLoaders = this._loadStatus.numberLoaders - 1;
    }
    this._loadStatus.loading = this._loadStatus.numberLoaders > 0;
    this._loadStatus.message = message;
  },

  setConfirmationDialogStatus(
    status,
    successCallback = null,
    data = null,
    title = null,
    message = null,
    okTitle = null,
    cancelTitle = null,
    onCancel = null,
    okActionType = '',
    cancelActionType = ''
  ) {
    this._confirmationDialog.active = status;
    this._confirmationDialog.successCallback = successCallback;
    this._confirmationDialog.data = data;
    this._confirmationDialog.title = title;
    this._confirmationDialog.message = message;
    this._confirmationDialog.okTitle = okTitle;
    this._confirmationDialog.cancelTitle = cancelTitle;
    this._confirmationDialog.cancelCallback = onCancel;
    this._confirmationDialog.okActionType = okActionType;
    this._confirmationDialog.cancelActionType = cancelActionType;

    this.setPopups('confirmation-dialog-popup', status);
  },

  getConfirmationDialogStatus() {
    return this._confirmationDialog;
  },

  resetOverlays() {
    this._allOverlays = {
      'general-responses-overlay': false,
      'step-priority-overlay': false,
    };
  },

  writeToFile(filename, data) {
    let textFile = null;
    const makeTextFile = function createFile(text) {
      const blobData = new Blob([text], { type: 'text/plain' });

      // If we are replacing a previously generated file we need to
      // manually revoke the object URL to avoid memory leaks.
      if (textFile !== null) {
        window.URL.revokeObjectURL(textFile);
      }

      textFile = window.URL.createObjectURL(blobData);

      // returns a URL you can use as a href
      return textFile;
    };

    const link = document.createElement('a');
    link.setAttribute('download', filename);
    link.href = makeTextFile(JSON.stringify(data));
    document.body.appendChild(link);

    // wait for the link to be added to the document
    window.requestAnimationFrame(() => {
      const event = new MouseEvent('click');
      link.dispatchEvent(event);
      document.body.removeChild(link);
    });
  },

  setComponentModalStatus(
    status,
    children = null,
    loading,
    title,
    subtitle,
    iconType,
    hasSaveButton,
    onIconClick,
    onSave,
    headerRight = null,
    className = '',
    isV2,
    headerIcon,
    headerSubIcon,
    onModalClose
  ) {
    this._componentModalStatus = {};
    this._componentModalStatus.active = status;
    this._componentModalStatus.children = children;
    this._componentModalStatus.loading = loading;
    this._componentModalStatus.title = title;
    this._componentModalStatus.subtitle = subtitle;
    this._componentModalStatus.iconType = iconType;
    this._componentModalStatus.hasSaveButton = hasSaveButton;
    this._componentModalStatus.onIconClick = onIconClick;
    this._componentModalStatus.onSave = onSave;
    this._componentModalStatus.headerRight = headerRight;
    this._componentModalStatus.className = className;
    this._componentModalStatus.isV2 = isV2;
    this._componentModalStatus.headerIcon = headerIcon;
    this._componentModalStatus.headerSubIcon = headerSubIcon;
    this._componentModalStatus.onModalClose = onModalClose;
  },

  getComponentModalStatus() {
    return this._componentModalStatus;
  },

  updateComponentModalStatus(params) {
    this._componentModalStatus = {
      ...this._componentModalStatus,
      ...params,
    };
  },

  setHeaderBrands(brandsList) {
    this._headerData.brandsList = brandsList;
  },

  setHeaderActions(headerActions) {
    this._headerData.actions = headerActions;
  },

  getHeaderData() {
    return this._headerData;
  },

  getComponentTooltipStatus() {
    return this._componentTooltipStatus;
  },

  setComponentTooltipStatus(
    active,
    positionX,
    positionY,
    children,
    type,
    destinationRect
  ) {
    this._componentTooltipStatus.active = active;
    this._componentTooltipStatus.positionX = positionX;
    this._componentTooltipStatus.positionY = positionY;
    this._componentTooltipStatus.children = children;
    this._componentTooltipStatus.type = type;
    this._componentTooltipStatus.destinationRect = destinationRect;
  },

  setCreateBotModalStatus(created, botId, setupData) {
    this._createBotModalStatus.created = created;
    this._createBotModalStatus.botId = botId;
    this._createBotModalStatus.setupData = setupData;
  },

  getCreateBotModalStatus() {
    return this._createBotModalStatus;
  },

  setCreateTemplateModalStatus(created, botId) {
    this._createTemplateModalStatus.created = created;
    this._createTemplateModalStatus.botId = botId;
  },

  getCreateTemplateModalStatus() {
    return this._createTemplateModalStatus;
  },
});

ActionStore.dispatchToken = register((action) => {
  const actionType = action.type;
  action = action.action;
  switch (actionType) {
    case 'OPEN_POPUP':
      ActionStore.setPopups(action.id, action.status);
      ActionStore.emitChange();
      break;

    case 'CLOSE_POPUP':
      ActionStore.setAlertStatus(false);
      ActionStore.setPopups(action.id, action.status);
      ActionStore.emitChange();
      break;

    case ActionTypes.OPEN_CONFIRMATION_DIALOG:
      ActionStore.setConfirmationDialogStatus(
        true,
        action.callback,
        action.data,
        action.title,
        action.message,
        action.okTitle,
        action.cancelTitle,
        action.onCancel,
        action.okActionType,
        action.cancelActionType
      );
      ActionStore.emitChange();
      break;
    case ActionTypes.CLOSE_CONFIRMATION_DIALOG:
      ActionStore.setConfirmationDialogStatus(false);
      ActionStore.emitChange();
      break;
    case 'OPEN_EDIT_BOT_DIALOG':
      ActionStore.setEditBotDialogStatus(true, action.data);
      ActionStore.emitChange();
      break;

    case 'CLOSE_OVERLAY':
      ActionStore.setOverlays(action.overlayId, action.status);
      ActionStore.emitChange();
      break;

    case 'RECEIVE_NEW_BOT':
      ActionStore.setPopups('create-bot-popup', false);
      ActionStore.emitChange();
      break;

    case 'CREATE_NEW_STORY':
      ActionStore.setPopups('create-new-story-popup', false);
      ActionStore.emitChange();
      break;

    case 'GET_ALL_STORIES':
    case 'GET_ALL_STEPS':
    case 'GET_STEP_INFO':
      ActionStore.resetOverlays();
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.START_LOADING:
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.STOP_LOADING:
      ActionStore.setLoadStatus(false, action.message);
      ActionStore.emitChange();
      break;

    case 'CREATE_ALERT':
      ActionStore.setAlertStatus(
        true,
        action.message,
        action.alertLength,
        action.messageType
      );
      ActionStore.emitChange();
      break;

    case 'DESTROY_ALERT':
      ActionStore.setAlertStatus(false);
      ActionStore.emitChange();
      break;

    case 'SEND_STEP_PRIORITIES':
    case 'SEND_GENERAL_RESPONSES':
    case 'SEND_USER_RESPONSES':
    case 'SEND_PARTNER_RESPONSES':
    case ActionTypes.UPDATE_SUB_STORIES:
    case 'GET_ALL_SUB_STORIES':
    case 'GET_NEGATIVE_RESPONSES':
    case 'GET_ALL_BOTS':
    case ActionTypes.SEND_SYNONYMS:
    case ActionTypes.GET_GENERAL_RESPONSES:
    case ActionTypes.GET_NODE_PRIORITY:
    case ActionTypes.SAVE_BOT_RESPONSES:
    case ActionTypes.ADD_NEW_SUB_STORY:
    case ActionTypes.SEND_ENTITIES:
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case 'RECEIVE_STEP_PRIORITIES':
    case 'RECEIVE_USER_RESPONSES':
    case ActionTypes.RECEIVE_DISAMBIGUATION_TEXTS:
    case 'RECEIVE_TRUTH_TABLE_RESPONSE':
    case 'RECEIVE_GENERAL_RESPONSES':
    case 'RECEIVE_ALL_SUB_STORIES':
    case ActionTypes.RECEIVE_ALL_BOTS:
    case ActionTypes.PARTNER_RESPONSE_RECEIVED:
    case ActionTypes.RECEIVE_MEANINGS:
    case ActionTypes.SET_GENERAL_NODES:
    case ActionTypes.BOT_RESPONSES_RECEIVED:
    case ActionTypes.SET_BOT_RESPONSES:
    case ActionTypes.UPDATE_GENERAL_NODES:
    case ActionTypes.RECEIVED_BUSINESS_LIST:
      ActionStore.setLoadStatus(false);
      ActionStore.emitChange();
      break;

    case 'TRANSFER_BOT':
      ActionStore.setLoadStatus(true, 'Transferring Bot info');
      ActionStore.emitChange();
      break;

    case ActionTypes.DEPLOY_BOT:
      ActionStore.setLoadStatus(true, 'Deploying Bot');
      ActionStore.emitChange();
      break;

    case 'EXTRACT_BOT':
      ActionStore.setLoadStatus(true, 'Extracting Bot info');
      ActionStore.emitChange();
      break;

    case 'UPLOAD_BOT':
      ActionStore.setLoadStatus(true, 'Uploading Bot info');
      ActionStore.emitChange();
      break;

    case 'UPLOAD_NEW_BOT':
      ActionStore.setLoadStatus(true, 'Uploading new bot');
      ActionStore.emitChange();
      break;

    case 'RECEIVE_BOT_EXTRACT':
      ActionStore.setLoadStatus(false, 'Bot Extract Completed');
      ActionStore.writeToFile(
        `${action.domain} ${new Date().toLocaleString()}.json`,
        action.data
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.RECEIVE_DEPLOY_BOT:
      {
        let alertDeployText =
          'Deployment Failed! Please try again after 2 minutes';
        if (action.data === true) {
          alertDeployText =
            'Bot Training Initiated! Check your mail in a minute to confirm success. Also, track steps with similar ‘user says’ to avoid faulty Step detection.';
        }

        ActionStore.setLoadStatus(false, 'Bot Deploy Completed!');
        ActionStore.setAlertStatus(true, alertDeployText, 1000);
        ActionStore.emitChange();
      }
      break;

    case 'RECEIVE_NEW_STEP_FAILED':
      ActionStore.setLoadStatus(false, 'Create Step failed!');
      ActionStore.setAlertStatus(
        true,
        'Create step failed!',
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.OPEN_COMPONENT_MODAL:
      ActionStore.setComponentModalStatus(
        true,
        action.children,
        false,
        action.title,
        action.subtitle,
        action.iconType,
        action.hasSaveButton,
        action.onIconClick,
        action.onSave,
        action.headerRight,
        action.className,
        action.isV2,
        action.headerIcon,
        action.headerSubIcon,
        action.onModalClose
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.CLOSE_COMPONENT_MODAL:
      ActionStore.setComponentModalStatus(false, null, false);
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_COMPONENT_MODAL:
      ActionStore.updateComponentModalStatus(action.params);
      ActionStore.emitChange();
      break;

    case ActionTypes.LOADING_COMPONENT_MODAL:
      ActionStore.setComponentModalStatus(
        true,
        null,
        true,
        action.title,
        action.subtitle,
        action.iconType,
        action.hasSaveButton
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_HEADER_BRANDS:
      ActionStore.setHeaderBrands(action.brandsList);
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_HEADER_ACTIONS:
      ActionStore.setHeaderActions(action.data);
      ActionStore.emitChange();
      break;

    case ActionTypes.OPEN_COMPONENT_TOOLTIP:
      ActionStore.setComponentTooltipStatus(
        true,
        action.position.x,
        action.position.y,
        action.children,
        action.type,
        action.destinationRect
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.CLOSE_COMPONENT_TOOLTIP:
      ActionStore.setComponentTooltipStatus(false, 0, 0);
      ActionStore.emitChange();
      break;

    case ActionTypes.FETCH_ALL_STORIES:
      ActionStore.setAlertStatus(false);
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.RECEIVE_ALL_STORIES_SUCCESS:
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(
          true,
          action.message,
          AlertTimeout,
          AlertTypes.POSITIVE
        );
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.RECEIVE_ALL_STORIES_FAILED:
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(
          true,
          action.message,
          AlertTimeout,
          AlertTypes.NEGATIVE
        );
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_STORIES:
      ActionStore.setAlertStatus(false);
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.CREATE_BOT_SUCCESS:
      ActionStore.setCreateBotModalStatus(
        true,
        action.data.bot_id,
        action.data.setup_data
      );
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(
          true,
          action.message,
          AlertTimeout,
          AlertTypes.POSITIVE
        );
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.CREATE_BOT_FAILED:
      ActionStore.setCreateBotModalStatus(false, null, null);
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(
          true,
          action.message,
          AlertTimeout,
          AlertTypes.NEGATIVE
        );
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_CREATE_BOT_MODAL_STATUS:
      ActionStore.setCreateBotModalStatus(
        action.created,
        action.botId,
        action.setupData
      );
      break;

    case ActionTypes.CREATE_BOT:
      ActionStore.setAlertStatus(false);
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.FETCH_BOT_TEST_CONFIG:
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.FETCH_BOT_TEST_CONFIG_SUCCESS:
    case ActionTypes.FETCH_BOT_TEST_CONFIG_FAILED:
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(true, action.message, AlertTimeout);
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.FETCH_TEMPLATES:
      ActionStore.setAlertStatus(false);
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.FETCH_TEMPLATES_SUCCESS:
    case ActionTypes.FETCH_TEMPLATES_FAILED:
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(true, action.message, AlertTimeout);
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.CREATE_TEMPLATES:
      ActionStore.setAlertStatus(false);
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.CREATE_TEMPLATES_SUCCESS:
      ActionStore.setCreateTemplateModalStatus(true, action.data.domain_id);
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(true, action.message, AlertTimeout);
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.CREATE_TEMPLATES_FAILED:
      ActionStore.setCreateTemplateModalStatus(false, null);
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(true, action.message, AlertTimeout);
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_CREATE_TEMPLATE_MODAL_STATUS:
      ActionStore.setCreateTemplateModalStatus(action.created, action.botId);
      ActionStore.setLoadStatus(false);
      if (action.message) {
        ActionStore.setAlertStatus(true, action.message, AlertTimeout);
      }
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_TEMPLATES_SUCCESS:
      ActionStore.setComponentModalStatus(false, null, false);
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.POSITIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_TEMPLATES_FAILED:
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.ADD_TEMPLATE_TO_BOT_SUCCESS:
      ActionStore.setComponentModalStatus(false, null, false);
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.POSITIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.ADD_TEMPLATE_TO_BOT_FAILED:
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.FETCH_ENTITY_DATA_FAILED:
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_ENTITY_DATA_SUCCESS:
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.POSITIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_ENTITY_DATA_FAILED:
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_BOT_FAILED:
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_BUSINESS_PROMPT_TRIGGER:
      ActionStore.setLoadStatus(true, action.message);
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_BUSINESS_PROMPT_TRIGGER_SUCCESS:
      ActionStore.setLoadStatus(false);
      ActionStore.emitChange();
      break;

    case ActionTypes.UPDATE_BUSINESS_PROMPT_TRIGGER_FAILED:
      ActionStore.setLoadStatus(false);
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    case ActionTypes.ADD_LANGUAGE_TO_BOT_SUCCESS:
      ActionStore.setLoadStatus(false);
      ActionStore.emitChange();
      break;

    case ActionTypes.ADD_LANGUAGE_TO_BOT_FAILED:
      ActionStore.setLoadStatus(false);
      ActionStore.setAlertStatus(
        true,
        action.message,
        AlertTimeout,
        AlertTypes.NEGATIVE
      );
      ActionStore.emitChange();
      break;

    default:
      break;
  }
});

export default ActionStore;
